import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from 'services/toast.service';
import { ApiService } from 'services/api.service';
import { NgClass } from '@angular/common';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { BaseFormModalComponent } from '../base-form-modal.component';
import {
  BackofficeReadingRoomSubmissionCard,
  CreateReadingRoomSubmissionNoteRequestDto,
  ReadingRoomSubmissionNoteCardDto,
  UpdateReadingRoomSubmissionNoteRequestDto
} from 'types/service.interface';
import { MAX_LENGTH_ADDITIONAL_INFO, MIN_LENGTH_DESCRIPTION, getControlsWithErrors } from 'util/form.utility';
import { FILE_OBJECT_TYPE_SUMBISSION_NOTE } from 'types/file.interface';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'app-submission-note-modal',
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, NgClass, SpinnerComponent, TooltipComponent, FileUploadComponent],
  templateUrl: './submission-note-modal.component.html',
  styleUrls: ['./submission-note-modal.component.scss']
})
export class SubmissionNoteModalComponent extends BaseFormModalComponent implements OnChanges, AfterViewInit {
  @Input() isOpen: boolean = false;
  @Input() note: ReadingRoomSubmissionNoteCardDto | null = null;
  @Input() application!: BackofficeReadingRoomSubmissionCard;
  @Output() update = new EventEmitter<ReadingRoomSubmissionNoteCardDto>();

  @ViewChild('content') content!: TemplateRef<any>;

  readonly maxLength = MAX_LENGTH_ADDITIONAL_INFO;
  readonly submissionNoteFileType = FILE_OBJECT_TYPE_SUMBISSION_NOTE;
  shouldRenderTooltip = false;

  isCrudLoading = false;
  isSubmittingFiles = false;
  noteInstance!: ReadingRoomSubmissionNoteCardDto;

  constructor(
    formBuilder: FormBuilder,
    modalConfig: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, modalConfig, modalService, toastService, translateService);
  }

  get text(): AbstractControl | null {
    return this.form.get('text');
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);
      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.open(this.content);
    }
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      text: [
        this.note?.text || '',
        [Validators.required, Validators.minLength(MIN_LENGTH_DESCRIPTION), Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]
      ]
    });
  }

  submit(): void {
    if (!this.form.valid) {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.updateClientNoteItem');
      return;
    }

    this.isCrudLoading = true;
    const textValue = this.text?.value;

    if (this.note?.id) {
      const dto: UpdateReadingRoomSubmissionNoteRequestDto = {
        id: this.note.id,
        text: textValue
      };
      this.apiService.updateReadingRoomSubmissionNote(dto).subscribe((response: ReadingRoomSubmissionNoteCardDto) => {
        this.toastService.showSuccess('updateSubmissionNoteSuccess', '', {
          shouldTranslate: true
        });
        this.close();
        this.note = response;
        this.isSubmittingFiles = true;
      });
    } else {
      const args: CreateReadingRoomSubmissionNoteRequestDto = {
        ...this.form.value,
        readingRoomSubmissionId: this.application.id
      };
      this.apiService.createReadingRoomSubmissionNote(args).subscribe((response: ReadingRoomSubmissionNoteCardDto) => {
        this.toastService.showSuccess('addSubmissionNoteSuccess', '', {
          shouldTranslate: true
        });
        this.close();
        this.note = response;
        this.isSubmittingFiles = true;
      });
    }
  }

  afterNoteSaved(): void {
    this.toastService.showSuccess('noteSaved', '', { shouldTranslate: true });
    this.isCrudLoading = false;
    this.isSubmittingFiles = true;
  }

  onFileUploadCompleted(observables: Observable<any>[]): void {
    if (!observables?.length) {
      this.finishSubmission();
      return;
    }

    forkJoin(observables).subscribe({
      next: () => this.finishSubmission(),
      error: () => {
        this.toastService.showRequestError();
        this.isCrudLoading = false;
      }
    });
  }

  private finishSubmission(): void {
    this.isSubmittingFiles = false;
    this.update.emit(this.noteInstance);
    this.close();
  }

  override close(): void {
    super.close();
    this.cancel.emit();
  }

  getFiles(): any[] {
    return this.noteInstance?.readingRoomSubmissionNoteFiles ?? this.note?.readingRoomSubmissionNoteFiles ?? [];
  }
}
